import React from 'react';
import HojaCaratula from './base/HojaCaratula';

// Etiquetas EXACTAS de las plantillas (templates/caratula/caratula_jubilacion_*.docx): el
// modal es una vista previa, así que lo que se lee acá tiene que coincidir renglón por
// renglón con el papel. `guardable` marca los que además de imprimirse vuelven al
// cliente/trámite (ver CaratulaJubilacionDocumentoService.persistirDatosGuardables).
//
// CAMPOS es la UNIÓN de las cuatro variantes; cada layout de abajo nombra solo los suyos.
const CAMPOS = {
    ley: { label: 'LEY', tipo: 'texto', guardable: true },
    ingreso: { label: 'INGRESO', tipo: 'fecha' },
    liq: {
        label: 'LIQ', tipo: 'texto',
        nota: 'Viene de la fecha de liquidar del trámite. Lo que edites acá va solo al documento: para cambiarla de verdad, editala en la ficha del trámite (al lado de la carpeta).',
    },
    pacto: { label: 'PACTO', tipo: 'sino' },
    cuota: { label: 'CUOTA', tipo: 'texto', guardable: true },
    miArgentina: { label: 'MI ARGENTINA', tipo: 'texto', guardable: true },

    claveFiscal: { label: 'CLAVE FISCAL', tipo: 'clave', guardable: true },
    claveCvss: { label: 'CLAVE CVSS', tipo: 'clave', guardable: true },
    referencia: { label: 'REFERENCIA', tipo: 'texto' },
    cidi: { label: 'CIDI', tipo: 'clave', guardable: true },

    apellidoNombre: { label: 'APELLIDO/NOMBRE', tipo: 'texto' },
    dniCuit: { label: 'DNI/CUIT', tipo: 'texto' },
    fechaNacimiento: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },
    nacionalidad: { label: 'NACIONALIDAD', tipo: 'texto', guardable: true },
    domicilio: {
        label: 'DOMICILIO', tipo: 'domicilio', estructurado: 'domicilioDniDatos', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },
    telefonos: {
        label: 'Nº TELEFONICOS', tipo: 'texto', campo: 'telefono',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },
    telefono: {
        label: 'Nº TELEFONICO', tipo: 'texto',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },
    mail: { label: 'MAIL', tipo: 'email', guardable: true },
    estadoCivil: { label: 'ESTADO CIVIL', tipo: 'estadoCivil', guardable: true },
    conyuge: { label: 'CON', tipo: 'texto', guardable: true },

    relacionDependenciaTotal: {
        label: 'RELACION DE DEPENDENCIA TOTAL', tipo: 'texto', campo: 'relacionDependencia',
    },
    relacionDependencia: { label: 'RELACION DE DEPENDENCIA', tipo: 'texto' },
    posterior1093: {
        label: '/ POSTERIOR AL 10/93', tipo: 'texto',
        nota: 'El sistema todavía no lo calcula. Lo que escribas acá va solo al documento.',
    },
    autonomoMonotributo: { label: 'AUTONOMO O MONOTRIBUTO', tipo: 'texto' },
    aportesProvincia: { label: 'APORTES PROVINCIALES', tipo: 'sino', guardable: true },
    cuentaAfip: { label: 'CUENTA EN AFIP', tipo: 'texto', guardable: true },
    cuentaAutonomo: { label: 'CUENTA AUTONOMO', tipo: 'texto', campo: 'cuentaAfip', guardable: true },
    planesVigentes: { label: 'PLANES VIGENTES', tipo: 'texto', guardable: true },

    regularidad: { label: 'REGULARIDAD', tipo: 'texto' },
    edad: { label: 'EDAD', tipo: 'texto' },
    patologiaDesde: { label: 'PATOLOGIA DESDE', tipo: 'texto', guardable: true },
    nivelEducativo: { label: 'NIVEL EDUCATIVO ALCANZADO', tipo: 'texto' },
    colegio: { label: 'COLEGIO', tipo: 'texto', guardable: true },
    patologia: { label: 'PATOLOGÍA', tipo: 'texto', guardable: true },
    turnoJuntaMedica: { label: '*TURNO 1° JUNTA MÉDICA', tipo: 'texto', guardable: true },
    resultadoJuntaMedica: { label: '- RESULTADO %', tipo: 'texto', guardable: true },
    apelacionPrimera: { label: '*1° APELACIÓN', tipo: 'texto', guardable: true },
    apelacionSegunda: { label: '* 2° APELACIÓN', tipo: 'texto', guardable: true },
    turnoPericia: { label: '*TURNO PERICIA MÉDICA', tipo: 'texto', guardable: true },

    observaciones: { label: 'OBSERVACIONES', tipo: 'multilinea', guardable: true },
    liquidar: {
        label: 'LIQUIDAR', tipo: 'texto', guardable: true,
        nota: 'Se prellena con la fecha de liquidar del trámite si está vacío. Admite texto libre ("a coordinar"). Guardarlo no modifica esa fecha.',
    },

    // ---- CAJA_PROVINCIA y CUIDADO_HIJOS. Los renglones nuevos son SOLO para el papel (no
    // tienen columna, decisión del estudio): no llevan `guardable`.
    leyRegimen: { label: 'Ley - Régimen', tipo: 'texto', campo: 'ley', guardable: true },
    leyHijos: { label: 'Jubilación Ley', tipo: 'texto', campo: 'ley', guardable: true },
    reco: { label: 'RECO', tipo: 'texto' },
    telefono1: {
        label: 'Nº TEL. 1', tipo: 'texto',
        nota: 'Teléfono propio del cliente. Solo se usa para este documento: se edita desde la ficha del cliente.',
    },
    telefono2: {
        label: 'Nº TEL. 2', tipo: 'texto',
        nota: 'Contacto adicional del cliente. Solo se usa para este documento: se edita desde la ficha del cliente.',
    },
    telefono3: {
        label: 'Nº TEL. 3', tipo: 'texto',
        nota: 'Contacto adicional del cliente (si hay más, van todos acá). Solo se usa para este documento.',
    },
    telefonoCidi: { label: 'Teléfono declarado en CIDI', tipo: 'texto' },
    mailCidi: { label: 'MAIL declarado en CIDI', tipo: 'email' },
    moratoria: { label: 'MORATORIA', tipo: 'texto' },
    bancoCobro: { label: 'BANCO QUE QUIERE COBRAR', tipo: 'texto' },
    subsidioFallecimiento: {
        label: 'SUBSIDIO POR FALLECIMIENTO (Nombre y Cuit de los beneficiarios)', tipo: 'multilinea',
    },
    cantidadHijos: {
        label: 'Hijos cantidad', tipo: 'texto',
        nota: 'Se cuenta de los hijos cargados en la ficha del cliente. Solo se usa para este documento.',
    },
    cantidadHijosCuidado: {
        label: 'CANTIDAD DE HIJOS', tipo: 'texto', campo: 'cantidadHijos',
        nota: 'Se cuenta de los hijos cargados en la ficha del cliente. Solo se usa para este documento.',
    },
    cuotaAlimentaria: { label: '¿Cuota alimentaria?', tipo: 'sino' },
    hijosDiscapacitados: {
        label: '¿Tiene hijos discapacitados?', tipo: 'sino',
        nota: 'SI si algún hijo de la ficha tiene CUD. Solo se usa para este documento.',
    },
    hijosMenores: {
        label: '¿Tiene hijos menores?', tipo: 'sino',
        nota: 'Se calcula con las fechas de nacimiento de los hijos de la ficha. Solo se usa para este documento.',
    },
    aportesProvinciaHijos: {
        label: 'APORTES PROVINCIA', tipo: 'sino', campo: 'aportesProvincia', guardable: true,
    },
    conAuh: {
        label: 'CON AUH', tipo: 'sino',
        nota: 'SI si algún hijo de la ficha tiene AUH. Solo se usa para este documento.',
    },
    conCud: {
        label: 'CON CUD', tipo: 'sino',
        nota: 'SI si algún hijo de la ficha tiene CUD. Solo se usa para este documento.',
    },
    tieneActas: { label: 'TIENE ACTAS', tipo: 'texto', placeholder: 'Ej: ella las busca' },
    iniciar: {
        label: 'INICIAR', tipo: 'texto',
        nota: 'Se prellena con la fecha de liquidar del trámite. Solo se usa para este documento.',
    },
};

const SEPARADOR = { separador: true };

// Jubilación tiene DOS carátulas y las dos sirven para cualquier trámite: la elige el
// operador desde el menú del botón (a diferencia de Reajuste, donde la deriva el backend de
// tipoReajuste). No son la misma carátula con otro título: la 27675 suma patología,
// regularidad, junta médica y apelaciones; la 24476 lleva NACIONALIDAD, APORTES
// PROVINCIALES, OBSERVACIONES y LIQUIDAR, que la otra no tiene.
const LAYOUTS = {
    LEY_24476: {
        titulo: 'JUBILACIÓN LEY 24.476',
        cabecera: [['ley', 'ingreso'], ['liq', 'pacto', 'cuota']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['fechaNacimiento'],
            ['nacionalidad'],
            ['domicilio'],
            ['telefonos'],
            ['mail'],
            ['estadoCivil', 'conyuge'],
            ['relacionDependenciaTotal', 'posterior1093'],
            ['autonomoMonotributo'],
            ['aportesProvincia'],
            ['cuentaAfip'],
            ['planesVigentes'],
            ['observaciones'],
            ['liquidar'],
        ],
    },
    LEY_27675: {
        titulo: 'JUBILACIÓN 27.675',
        cabecera: [['ley', 'ingreso', 'liq'], ['pacto', 'cuota', 'miArgentina']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['fechaNacimiento'],
            ['domicilio'],
            ['telefono'],
            ['mail'],
            ['estadoCivil', 'conyuge'],
            ['relacionDependencia'],
            ['autonomoMonotributo'],
            ['cuentaAutonomo', 'planesVigentes'],
            ['regularidad', 'edad'],
            ['patologiaDesde'],
            ['nivelEducativo', 'colegio'],
            ['patologia'],
            ['turnoJuntaMedica', 'resultadoJuntaMedica'],
            ['apelacionPrimera', 'apelacionSegunda'],
            ['turnoPericia'],
        ],
    },
    // Calcadas del papel: sin cajitas de cabecera (los renglones de arriba van sueltos), con
    // las líneas que separan los bloques y sin pie.
    CAJA_PROVINCIA: {
        titulo: ['DATOS PERSONALES', 'Jubilación: Caja de la provincia'],
        cabecera: [],
        filasPrevias: [
            ['leyRegimen'],
            ['ingreso'],
            ['pacto'],
            ['reco'],
            ['claveFiscal', 'cidi'],
            ['referencia', 'claveCvss'],
        ],
        cuerpo: [
            { keys: ['apellidoNombre', 'dniCuit'], apilado: true },
            SEPARADOR,
            ['fechaNacimiento'],
            SEPARADOR,
            ['domicilio'],
            SEPARADOR,
            ['telefono1', 'telefono2'],
            SEPARADOR,
            ['telefonoCidi'],
            SEPARADOR,
            ['mailCidi'],
            SEPARADOR,
            ['estadoCivil', 'conyuge'],
            SEPARADOR,
            ['relacionDependencia'],
            SEPARADOR,
            ['autonomoMonotributo', 'cuentaAfip'],
            SEPARADOR,
            ['moratoria'],
            SEPARADOR,
            ['bancoCobro'],
            SEPARADOR,
            ['subsidioFallecimiento'],
            SEPARADOR,
            ['cantidadHijos', 'cuotaAlimentaria', 'hijosDiscapacitados'],
            SEPARADOR,
            ['hijosMenores'],
            SEPARADOR,
            ['liquidar'],
        ],
        pie: null,
    },
    CUIDADO_HIJOS: {
        titulo: ['DATOS PERSONALES'],
        leyenda: '-Ley por hijos -',
        cabecera: [],
        filasPrevias: [
            ['leyHijos'],
            ['ingreso'],
            ['pacto'],
            ['claveFiscal', 'claveCvss'],
            ['referencia'],
        ],
        cuerpo: [
            { keys: ['apellidoNombre', 'dniCuit'], apilado: true },
            SEPARADOR,
            { keys: ['fechaNacimiento', 'nacionalidad'], apilado: true },
            SEPARADOR,
            ['domicilio'],
            SEPARADOR,
            ['telefono1', 'telefono2', 'telefono3'],
            SEPARADOR,
            ['mail'],
            SEPARADOR,
            ['estadoCivil', 'conyuge'],
            SEPARADOR,
            ['relacionDependencia'],
            SEPARADOR,
            ['autonomoMonotributo'],
            SEPARADOR,
            ['aportesProvinciaHijos'],
            SEPARADOR,
            ['cuentaAfip'],
            SEPARADOR,
            ['planesVigentes'],
            SEPARADOR,
            ['cantidadHijosCuidado'],
            SEPARADOR,
            ['conAuh', 'conCud'],
            SEPARADOR,
            ['tieneActas'],
            SEPARADOR,
            ['iniciar'],
        ],
        pie: null,
    },
};

// CLAVE FISCAL/CVSS y REFERENCIA/CIDI van igual en las dos y se dibujan aparte, arriba del
// recuadro, tal como están en el papel.
const FILA_CLAVES = ['claveFiscal', 'claveCvss'];
const FILA_REFERENCIA = ['referencia', 'cidi'];

// CLAVE FISCAL/CVSS y REFERENCIA van igual en las dos variantes y se dibujan aparte, arriba
// del recuadro, tal como están en el papel.
const FILAS_PREVIAS = [FILA_CLAVES, FILA_REFERENCIA];

// La variante la elige el operador en el menú del botón y viaja en `datos.variante`; ante
// una desconocida se cae a la 24476, que es la carátula simple.
const hojaDe = (form) => {
    const layout = LAYOUTS[form?.variante] || LAYOUTS.LEY_24476;
    return {
        tituloDoc: layout.titulo,
        leyendaDoc: layout.leyenda,
        cabecera: layout.cabecera,
        filasPrevias: layout.filasPrevias || FILAS_PREVIAS,
        cuerpo: layout.cuerpo,
        pie: layout.pie,
    };
};

/**
 * Modal de la Carátula de Jubilación. Dibuja el layout de la variante que corresponda y
 * muestra los renglones prellenados desde el cliente/trámite, en blanco lo que falta. Al
 * confirmar, persiste los renglones guardables y emite el .docx con el DTO tal cual quedó en
 * el formulario (WYSIWYG).
 */
export default function ModalCaratulaJubilacion(props) {
    return (
        <HojaCaratula
            {...props}
            titulo="Generar Carátula de Jubilación"
            nombreDocumento="la Carátula de Jubilación"
            campos={CAMPOS}
            layout={hojaDe}
        />
    );
}
